/**
 * THE REPORT BATCH - the Reporting page's `Push All Ready To TMS`, through the TMS Push extension.
 *
 * Authority: the TD's request of 2 October 2026 - sixteen technical-panel officials (three TOs and
 * thirteen Judges, every report drafted and every mark set) still to push, the tournament ending the
 * next day, and ONE action wanted instead of sixteen. Everything one press does is unchanged
 * (`reportPushFlow.ts`); the batch is that flow for every ready official at once:
 *
 *   hello (3 s)  ->  POST /reports/push/batch/prepare { presses }  ->  push-report-batch over a port
 *                ->  POST .../report/push/report { pressId, result }, per official, as each result
 *                    arrives  ->  ack, per official
 *
 * THE HONEST STATES, the single press's, per official:
 *  * no extension answers, or it will not take the batch: nothing was sent and nothing reported -
 *    the page says what to check;
 *  * an official the prepare refused (a gate, or already in TMS) was never queued: named, with why;
 *  * a result: reported to the API exactly as the extension sent it, as soon as it arrives - so a
 *    page closed half-way through leaves every official already done with their receipts - and the
 *    API decides: saved ONLY when it recorded `accepted_draft` (a read-back that is the plan);
 *  * the port lost or unreadable: the page asks the extension what it KEPT for each press still
 *    without a result; with nothing to go on, that official may or may not have been saved - check
 *    TMS. Never a guess, never a success that was not recorded.
 *
 * A prepare or report whose answer was lost is asked ONCE more with the same press ids (the API
 * answers known presses from their records), and never more: a press is never repeated.
 */

import { reportBatchHeadline, type TmsReportPushPlan, type TmsReportPushResultMessage } from "@hockeytd/shared";

import { ApiError, prepareReportPushBatch, reportReportPush } from "../../api/client.js";
import type { ReportPushBatchPrepareBody, ReportPushReadyEntry, ReportPushReportBody } from "../../api/types.js";
import { LIVE_TMS_PUSH_BRIDGE, TMS_PUSH_PRESS_HELLO_TIMEOUT_MS, type TmsPushBridge, type TmsReportBatchPortEnding } from "../appointments/tmsPushExtension.js";
import { EXTENSION_ABSENT_TEXT, REPORT_PUSH_STAGE_WORDS, ReportPushNotLanded, outcomeUnknownText } from "./reportPushFlow.js";

/** One official's line in the page's closing summary. */
export interface ReportBatchLine {
  readonly tmsPersonId: string;
  /** `KIM Seong Eun (Judge)`. */
  readonly who: string;
  /** True ONLY when the API recorded the push as accepted - a save read back as planned. */
  readonly saved: boolean;
  readonly words: string;
}

/** How a batch ended, as the page reports it: the shared headline, and every official. */
export interface ReportBatchOutcome {
  readonly saved: number;
  readonly failed: number;
  readonly headline: string;
  readonly lines: readonly ReportBatchLine[];
}

export interface ReportBatchFlowDeps {
  readonly bridge: TmsPushBridge;
  readonly prepareBatch: (presses: readonly { tmsPersonId: string; pressId: string }[]) => Promise<ReportPushBatchPrepareBody>;
  readonly report: (tmsPersonId: string, pressId: string, result: TmsReportPushResultMessage) => Promise<ReportPushReportBody>;
  readonly newPressId: () => string;
}

const LIVE_DEPS: ReportBatchFlowDeps = {
  bridge: LIVE_TMS_PUSH_BRIDGE,
  prepareBatch: prepareReportPushBatch,
  report: reportReportPush,
  newPressId: () => crypto.randomUUID(),
};

function lostAnswer(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || error.status >= 500);
}

async function onceMore<T>(ask: () => Promise<T>): Promise<T> {
  try {
    return await ask();
  } catch (error) {
    if (!lostAnswer(error)) throw error;
    return ask();
  }
}

/** Why the extension never had the batch in hand: nothing was sent. Null when it may have. */
function notTakenWords(ending: TmsReportBatchPortEnding): string | null {
  switch (ending.kind) {
    case "unavailable":
      return EXTENSION_ABSENT_TEXT;
    case "busy":
      return "Another push from this page is still out in the TMS Push extension. Finish or cancel it there first. Nothing was sent to TMS for this batch.";
    case "too-large":
      return "This batch is too large to hand to the TMS Push extension in one message, so nothing was sent to TMS. Push the officials in smaller groups from their own pages.";
    case "refused":
      return ending.reason === "busy" || ending.reason === "invalid-message" ? `The TMS Push extension would not take this batch: ${ending.detail}` : null;
    default:
      return null;
  }
}

type Recorded = { readonly kind: "recorded"; readonly body: ReportPushReportBody } | { readonly kind: "not-recorded"; readonly why: string };

function recordedLine(entry: { readonly tmsPersonId: string; readonly who: string }, recorded: Recorded | undefined, pressId: string): ReportBatchLine {
  if (recorded === undefined) return { ...entry, saved: false, words: outcomeUnknownText(pressId) };
  if (recorded.kind === "not-recorded") {
    return {
      ...entry,
      saved: false,
      words: `The TMS Push extension sent a result, but the Control Centre could not record it (${recorded.why}). Check the official's marking in TMS by eye before pushing again (press ${pressId}).`,
    };
  }
  const { body } = recorded;
  const receipt = body.receipts.find((entry) => entry.kind === "report") ?? body.receipts[0];
  if (body.outcome === "accepted_draft") {
    const stage = body.stageRefusal === null ? "" : ` But the report's stage here did not move: ${body.stageRefusal}`;
    return { ...entry, saved: true, words: `Saved in TMS as a draft, and checked by reading it back.${stage}` };
  }
  return { ...entry, saved: false, words: `NOT saved: ${receipt?.detail ?? "the Control Centre recorded this push as failed."}` };
}

/**
 * The Reporting page's `Push All Ready To TMS` - one human press for every ready official, through
 * the extension's one window and one Confirm. Resolves with every official's line, as the API
 * recorded it (and every official the prepare refused); throws {@link ReportPushNotLanded} when
 * nothing at all reached the extension, or the API's own `ApiError` when the prepare failed.
 */
export function createExtensionReportBatchPusher(deps: ReportBatchFlowDeps = LIVE_DEPS) {
  return async function pushBatch(entries: readonly ReportPushReadyEntry[], onProgress: (words: string) => void = () => undefined): Promise<ReportBatchOutcome> {
    const { bridge } = deps;
    if (bridge.pushReportBatch === undefined) throw new ReportPushNotLanded(EXTENSION_ABSENT_TEXT);
    const presence = await bridge.hello(TMS_PUSH_PRESS_HELLO_TIMEOUT_MS);
    if (presence.kind === "absent") throw new ReportPushNotLanded(EXTENSION_ABSENT_TEXT);

    const presses = entries.map((entry) => ({ tmsPersonId: entry.tmsPersonId, pressId: deps.newPressId() }));
    onProgress(`Preparing ${String(presses.length)} ${presses.length === 1 ? "push" : "pushes"} in the Control Centre…`);
    const prepared = await onceMore(() => deps.prepareBatch(presses));
    const plans: readonly TmsReportPushPlan[] = prepared.plans;
    const whoOf = (plan: TmsReportPushPlan): string => `${plan.subject.mediaName} (${plan.subject.role})`;
    const refusedLines: ReportBatchLine[] = prepared.refused.map((refusal) => {
      const entry = entries.find((candidate) => candidate.tmsPersonId === refusal.tmsPersonId);
      const who = entry === undefined ? (refusal.mediaName ?? `TMS person ${refusal.tmsPersonId}`) : `${entry.mediaName} (${entry.role})`;
      return { tmsPersonId: refusal.tmsPersonId, who, saved: false, words: `Not queued - nothing was sent to TMS: ${refusal.message}` };
    });
    if (plans.length === 0) {
      return { saved: 0, failed: refusedLines.length, headline: reportBatchHeadline(0, refusedLines.length), lines: refusedLines };
    }

    // Each result is reported the moment it arrives, one at a time, in plan order.
    const recorded = new Map<string, Recorded>();
    const resulted = new Set<string>();
    let chain: Promise<void> = Promise.resolve();
    const enqueue = (plan: TmsReportPushPlan, result: TmsReportPushResultMessage): void => {
      resulted.add(plan.pressId);
      chain = chain.then(async () => {
        try {
          const body = await onceMore(() => deps.report(plan.subject.tmsPersonId, plan.pressId, result));
          recorded.set(plan.pressId, { kind: "recorded", body });
          // Only now may the extension forget its copy: the API has the result.
          void bridge.ack(plan.pressId).catch(() => undefined);
        } catch (error) {
          recorded.set(plan.pressId, { kind: "not-recorded", why: error instanceof Error ? error.message : "no answer" });
        }
      });
    };

    onProgress("The TMS Push window has opened: check every official there, then Confirm - or Cancel.");
    const ending = await bridge.pushReportBatch(plans, {
      progress: (stage, pressId) => {
        const plan = plans.find((candidate) => candidate.pressId.toLowerCase() === pressId);
        if (stage === "awaiting-confirm" || plan === undefined) return;
        onProgress(`${whoOf(plan)}: ${REPORT_PUSH_STAGE_WORDS[stage]}`);
      },
      result: (index, result) => {
        const plan = plans[index];
        if (plan !== undefined) enqueue(plan, result);
      },
    });

    const notTaken = notTakenWords(ending);
    if (notTaken !== null && resulted.size === 0) throw new ReportPushNotLanded(notTaken);
    if (ending.kind !== "done") {
      // The extension may have run presses the port did not tell of: ask what it KEPT for each.
      for (const plan of plans) {
        if (resulted.has(plan.pressId) || bridge.reportStatus === undefined) continue;
        const status = await bridge.reportStatus(plan.pressId);
        if (status.kind === "result") enqueue(plan, status.result);
      }
    }
    onProgress("Recording what TMS saved…");
    await chain;

    const lines = [
      ...plans.map((plan) => recordedLine({ tmsPersonId: plan.subject.tmsPersonId, who: whoOf(plan) }, recorded.get(plan.pressId), plan.pressId)),
      ...refusedLines,
    ];
    const saved = lines.filter((line) => line.saved).length;
    return { saved, failed: lines.length - saved, headline: reportBatchHeadline(saved, lines.length - saved), lines };
  };
}

/** The live batch pusher, for the Reporting page. */
export const pushReportBatchThroughExtension = createExtensionReportBatchPusher();
